import { Video } from 'lucide-react';
import dashStyles from '../../pages/admin/AdminDashboardPage.module.css';
import { fmtCostoOpc, fmtNumero } from '../../utils/usoIaFormato';
import type { ConsumoPlanDoc } from './costosUtil';
import type { MinutosPorRol } from './serviciosAgregado';
import styles from './ServiciosUsoPanel.module.css';

interface Barra {
  titulo: string;
  usado: number | undefined;
  incluido: number | undefined;
  pct: number | undefined;
  unidad: string;
  nota?: string;
}

function BarraPlan({ b }: { b: Barra }) {
  if (b.usado == null || b.incluido == null) {
    return (
      <div className={styles.planItem}>
        <div className={styles.planItemHead}><strong>{b.titulo}</strong></div>
        <span className={styles.muted}>Sin dato todavía</span>
      </div>
    );
  }
  const frac = b.pct ?? (b.incluido > 0 ? b.usado / b.incluido : 0);
  const clamp = Math.max(0, Math.min(1, frac));
  const nivel = frac > 1 ? 'excedido' : frac >= 0.8 ? 'aviso' : 'ok';
  const texto = nivel === 'excedido' ? 'Excedido' : nivel === 'aviso' ? 'Cerca del límite' : 'Dentro de lo incluido';
  const clsTxt = nivel === 'excedido' ? styles.excedido : nivel === 'aviso' ? styles.aviso : styles.ok;
  const clsFill = nivel === 'excedido' ? styles.fillExcedido : nivel === 'aviso' ? styles.fillAviso : styles.fillOk;
  const pctTxt = `${fmtNumero(frac * 100, 1)} %`;
  return (
    <div className={styles.planItem}>
      <div className={styles.planItemHead}>
        <strong>{b.titulo}</strong>
        <span>{pctTxt}</span>
      </div>
      <div
        className={dashStyles.progressTrack}
        role="progressbar"
        aria-label={`${b.titulo}: ${pctTxt} de lo incluido`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(clamp * 100)}
      >
        <div className={`${dashStyles.progressFill} ${clsFill}`} style={{ width: `${clamp * 100}%` }} />
      </div>
      <span className={styles.planDet}>
        {fmtNumero(b.usado, 1)} de {fmtNumero(b.incluido, 0)} {b.unidad} incluidos
        {b.nota ? ` · ${b.nota}` : ''}
      </span>
      <span className={`${styles.estadoTxt} ${clsTxt}`}>{texto}</span>
    </div>
  );
}

interface Props {
  plan: ConsumoPlanDoc | null;
  cargando: boolean;
  roles: MinutosPorRol;
  desde: string;
  hasta: string;
}

/** Sección «Videollamadas (LiveKit)»: consumo del plan del mes y minutos por rol del rango. */
export default function PlanLivekitSeccion({ plan, cargando, roles, desde, hasta }: Props) {
  const sumaRoles = roles.medico + roles.paciente + roles.agente + roles.sinConsulta;
  const partes = [
    { clave: 'medico', nombre: 'Médico', valor: roles.medico, color: '#6366f1' },
    { clave: 'paciente', nombre: 'Paciente', valor: roles.paciente, color: '#14b8a6' },
    { clave: 'agente', nombre: 'Agente (IA)', valor: roles.agente, color: '#f59e0b' },
    { clave: 'sinConsulta', nombre: 'Salas sin consulta (triage por voz u otro entorno)', valor: roles.sinConsulta, color: '#94a3b8' },
  ];

  return (
    <div className={dashStyles.section}>
      <div className={dashStyles.sectionHeader}>
        <h2 className={dashStyles.sectionTitle}><Video size={16} aria-hidden="true" /> Videollamadas (LiveKit)</h2>
        {plan?.plan && <span className={dashStyles.sectionBadge}>Plan {plan.plan}</span>}
      </div>
      <div className={dashStyles.sectionBody}>
        <h3 className={styles.subtitulo} style={{ marginTop: 0 }}>
          Plan del mes{plan?.mes ? ` (${plan.mes})` : ''}
        </h3>
        {cargando ? (
          <p className={dashStyles.emptyState}>Cargando…</p>
        ) : !plan ? (
          <p className={dashStyles.emptyState}>
            Aún no hay datos del plan de este mes: se calculan con cada actualización del monitoreo.
          </p>
        ) : (
          <>
            <div className={styles.planGrid}>
              <BarraPlan b={{ titulo: 'Minutos de participante', usado: plan.minutos, incluido: plan.minutosIncluidos, pct: plan.pctMinutos, unidad: 'min' }} />
              <BarraPlan b={{ titulo: 'Grabación por pista', usado: plan.egressMin, incluido: plan.egressIncluidos, pct: plan.pctEgress, unidad: 'min' }} />
              <BarraPlan b={{
                titulo: 'Transferencia de datos', usado: plan.gb, incluido: plan.gbIncluidos, pct: plan.pctGb, unidad: 'GB',
                nota: plan.transferencia === 'real' ? 'dato real de LiveKit' : 'estimada por minutos, no medida',
              }} />
            </div>
            <div className={styles.cifras}>
              <div className={styles.cifra}>
                <p className={styles.cifraLbl}>Cuota del plan</p>
                <p className={styles.cifraVal}>{fmtCostoOpc(plan.cuotaUsd)}</p>
              </div>
              <div className={styles.cifra}>
                <p className={styles.cifraLbl}>Excedente a la fecha</p>
                <p className={styles.cifraVal}>{fmtCostoOpc(plan.excedenteUsd)}</p>
              </div>
              <div className={styles.cifra}>
                <p className={styles.cifraLbl}>Total del mes</p>
                <p className={styles.cifraVal}>{fmtCostoOpc(plan.totalMesUsd)}</p>
              </div>
              <div className={styles.cifra}>
                <p className={styles.cifraLbl}>Proyección al cierre</p>
                <p className={styles.cifraVal}>{fmtCostoOpc(plan.proyeccionMesUsd)}</p>
                {plan.diasTranscurridos != null && plan.diasDelMes != null && (
                  <p className={styles.muted}>día {plan.diasTranscurridos} de {plan.diasDelMes}</p>
                )}
              </div>
            </div>
          </>
        )}

        <h3 className={styles.subtitulo}>Minutos de participante por rol ({desde} a {hasta})</h3>
        {sumaRoles <= 0 ? (
          <p className={dashStyles.emptyState}>
            Sin minutos con desglose por rol en este rango.{' '}
            {roles.total > 0
              ? `Hay ${fmtNumero(roles.total, 1)} min en total, pero sin detalle de quién estaba en la sala.`
              : 'Se llenan con cada llamada que termina.'}
          </p>
        ) : (
          <>
            <div
              className={dashStyles.stackedBar}
              role="img"
              aria-label={partes.map((p) => `${p.nombre}: ${fmtNumero(p.valor, 1)} minutos`).join('; ')}
            >
              {partes.filter((p) => p.valor > 0).map((p) => (
                <div
                  key={p.clave}
                  className={dashStyles.stackedSegment}
                  style={{ flex: p.valor, background: p.color }}
                  title={`${p.nombre}: ${fmtNumero(p.valor, 1)} min`}
                >
                  {Math.round((p.valor / sumaRoles) * 100)}%
                </div>
              ))}
            </div>
            <div className={dashStyles.stackedLegend}>
              {partes.map((p) => (
                <span key={p.clave} className={dashStyles.legendItem}>
                  <span className={dashStyles.legendDot} style={{ background: p.color }} />
                  {p.nombre}: <strong>{fmtNumero(p.valor, 1)} min</strong>
                </span>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
